/*
 * Cart drawer helpers (native Dawn drawer):
 *  - <cart-upgrade-button>   : swap a line for a larger / bundle product
 *  - <cart-subscribe-button> : convert a one-time line to a subscription selling plan
 *  - [data-rec-add]          : add a "You may also like" product to the cart
 * All of them re-render the drawer in place afterwards.
 */
(function () {
  function cfg() {
    return typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
  }

  async function refreshCartDrawer() {
    const resp = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
    const doc = new DOMParser().parseFromString(await resp.text(), 'text/html');

    ['cart-drawer-items', '.cart-drawer__footer', '.cart__ctas'].forEach((sel) => {
      const tgt = document.querySelector(sel);
      const src = doc.querySelector(sel);
      if (tgt && src) tgt.replaceWith(src);
    });

    try {
      const b = await fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`);
      const bd = new DOMParser().parseFromString(await b.text(), 'text/html');
      const bTgt = document.querySelector('#cart-icon-bubble');
      const bSrc = bd.querySelector('#cart-icon-bubble');
      if (bTgt && bSrc) bTgt.innerHTML = bSrc.innerHTML;
    } catch (e) {
      /* non-fatal */
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.classList.remove('is-empty');
      const inner = drawer.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
      if (!drawer.classList.contains('active')) drawer.open && drawer.open();
    }
    nudgeRecs();
  }

  // The <product-recommendations> IntersectionObserver often doesn't fire inside
  // an off-screen drawer — nudge it directly.
  function nudgeRecs() {
    document.querySelectorAll('cart-drawer product-recommendations').forEach((el) => {
      if (el.dataset.nudged || !el.dataset.productId || typeof el.loadRecommendations !== 'function') return;
      el.dataset.nudged = '1';
      if (el.observer && el.observer.unobserve) el.observer.unobserve(el);
      el.loadRecommendations(el.dataset.productId);
    });
  }

  function broadcast(cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-helpers', cartData: cartData || {} });
    }
  }

  function showError() {
    const errBox = document.getElementById('CartDrawer-CartErrors');
    if (errBox) errBox.textContent = (window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.';
  }

  /* ---- <cart-upgrade-button> ---- */
  if (!customElements.get('cart-upgrade-button')) {
    class CartUpgradeButton extends HTMLElement {
      constructor() {
        super();
        this.btn = this.querySelector('.cart-item__upgrade-button');
        if (this.btn) this.btn.addEventListener('click', this.onClick.bind(this));
      }

      setLoading(on) {
        if (!this.btn) return;
        this.btn.toggleAttribute('aria-busy', on);
        this.btn.disabled = on;
        const spinner = this.btn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !on);
      }

      async onClick() {
        if (this.busy) return;
        this.busy = true;
        this.setLoading(true);
        const currentKey = this.dataset.currentKey;
        const upgradeId = parseInt(this.dataset.upgradeId, 10);
        try {
          const addRes = await fetch(window.routes.cart_add_url, {
            ...cfg(),
            body: JSON.stringify({ items: [{ id: upgradeId, quantity: 1 }] }),
          });
          if (!addRes.ok) {
            const err = await addRes.json().catch(() => ({}));
            throw new Error(err.description || 'add failed');
          }
          const changeRes = await fetch(window.routes.cart_change_url, {
            ...cfg(),
            body: JSON.stringify({ id: currentKey, quantity: 0 }),
          });
          const cartData = await changeRes.json();
          await refreshCartDrawer();
          broadcast(cartData);
        } catch (e) {
          console.error('[cart-upgrade]', e);
          showError();
          this.busy = false;
          this.setLoading(false);
        }
      }
    }
    customElements.define('cart-upgrade-button', CartUpgradeButton);
  }

  /* ---- <cart-subscribe-button> ---- */
  if (!customElements.get('cart-subscribe-button')) {
    class CartSubscribeButton extends HTMLElement {
      constructor() {
        super();
        this.btn = this.querySelector('.cart-item__subscribe-button');
        if (this.btn) this.btn.addEventListener('click', this.onClick.bind(this));
      }

      setLoading(on) {
        if (!this.btn) return;
        this.btn.toggleAttribute('aria-busy', on);
        this.btn.disabled = on;
        const spinner = this.btn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !on);
      }

      async onClick() {
        if (this.busy) return;
        this.busy = true;
        this.setLoading(true);
        const lineKey = this.dataset.lineKey;
        const sellingPlan = parseInt(this.dataset.sellingPlan, 10);
        try {
          const res = await fetch(window.routes.cart_change_url, {
            ...cfg(),
            body: JSON.stringify({ id: lineKey, selling_plan: sellingPlan }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || 'subscribe failed');
          }
          const cartData = await res.json();
          await refreshCartDrawer();
          broadcast(cartData);
        } catch (e) {
          console.error('[cart-subscribe]', e);
          showError();
          this.busy = false;
          this.setLoading(false);
        }
      }
    }
    customElements.define('cart-subscribe-button', CartSubscribeButton);
  }

  /* ---- nudge recommendations when the drawer opens or the cart re-renders ---- */
  function watchDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      setTimeout(watchDrawer, 300);
      return;
    }
    new MutationObserver(() => {
      if (drawer.classList.contains('active')) setTimeout(nudgeRecs, 30);
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    if (drawer.classList.contains('active')) nudgeRecs();
  }
  if (document.readyState !== 'loading') watchDrawer();
  else document.addEventListener('DOMContentLoaded', watchDrawer);

  // native Dawn cart updates re-render the whole drawer inner — re-nudge the fresh recs element
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(nudgeRecs, 60));
  }

  /* ---- recommendation "Add" buttons ---- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-rec-add]');
    if (!btn || btn.dataset.busy) return;
    e.preventDefault();
    btn.dataset.busy = '1';
    btn.setAttribute('aria-busy', 'true');
    try {
      await fetch(window.routes.cart_add_url, {
        ...cfg(),
        body: JSON.stringify({ items: [{ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }] }),
      });
      await refreshCartDrawer();
      broadcast();
    } catch (err) {
      console.error('[cart-rec-add]', err);
      showError();
    }
    delete btn.dataset.busy;
    btn.removeAttribute('aria-busy');
  });
})();
